/**
 * Aprobación HHEE — celda editable "Horas a aprobar" del detalle (ASIS-5356).
 *
 * Vive fuera de los hooks porque lo consumen dos: `useOvertimeApprovalDetail` (para pintar la
 * celda y decidir si va en rojo) y `useOvertimeApprovalDetailActions` (para bloquear la
 * aprobación y armar el payload). Tenerlo aquí evita que un hook importe al otro solo por esto.
 */

import { convertDecimalToTime, parseHoursInput } from '@/utils/timeConversion';
import {
  EMPTY_HOUR_VALUE,
  type OvertimeApprovalDetailRow,
} from '@/api/domains/overtime/overtimeApproval.types';

import { formatOvertimeHours, type OvertimeHourFormat } from './overtimeApprovalCounters';

/**
 * Tolerancia al comparar horas decimales contra las calculadas. Un segundo son 0,000277 horas, así
 * que 1e-4 queda por debajo del segundo: absorbe el error de redondeo de convertir HH:MM:SS ↔
 * decimal sin llegar a aceptar una diferencia perceptible para el usuario.
 */
const HOURS_COMPARISON_EPSILON = 1e-4;

export interface OvertimeDetailRowHours {
  /** Texto que debe mostrar la celda: lo tecleado, o el valor de la fila ya formateado. */
  text: string;
  /** Horas decimales resultantes, o `null` si lo tecleado no se puede interpretar. */
  decimalValue: number | null;
  /** `true` cuando el valor no es interpretable o supera las horas calculadas de la fila. */
  isInvalid: boolean;
}

/**
 * Formatea horas decimales sueltas (lo tecleado por el usuario, que no trae el `hhmmss` ya
 * resuelto por el backend) con el mismo criterio que `formatOvertimeHours`.
 */
export function formatOvertimeDecimalHours(
  decimalHours: number,
  hourFormat: OvertimeHourFormat,
): string {
  return formatOvertimeHours(
    {
      hhmmss: convertDecimalToTime(decimalHours) ?? EMPTY_HOUR_VALUE.hhmmss,
      decimalValue: decimalHours,
    },
    hourFormat,
  );
}

/**
 * Resuelve qué mostrar y qué valor tiene la celda "Horas a aprobar" de una fila. Sin entrada en
 * `editedText` la celda muestra el valor que trajo el backend; con ella manda lo tecleado.
 *
 * El número de esta celda es el TOTAL con el que quedará aprobado el tramo, no un incremento sobre
 * las horas ya aprobadas: viaja tal cual al SP legado, que reemplaza la jornada completa. Por eso
 * `row.hoursToApprove` llega del backend como el total propuesto (las horas calculadas) y no como
 * las pendientes — así aprobar sin editar no baja una aprobación previa.
 */
export function resolveDetailRowHours(
  row: OvertimeApprovalDetailRow,
  editedText: string | undefined,
  hourFormat: OvertimeHourFormat,
): OvertimeDetailRowHours {
  if (editedText === undefined) {
    return {
      text: formatOvertimeHours(row.hoursToApprove, hourFormat),
      decimalValue: row.hoursToApprove.decimalValue,
      isInvalid: false,
    };
  }

  const decimalValue = parseHoursInput(editedText, hourFormat);

  return {
    text: editedText,
    decimalValue,
    isInvalid:
      decimalValue === null
      || decimalValue > row.calculatedHours.decimalValue + HOURS_COMPARISON_EPSILON,
  };
}

/**
 * Reformatea lo ya tecleado al cambiar el switch de formato, en vez de descartarlo: el usuario no
 * debería perder ediciones por mirar las horas en el otro formato. Lo que no se puede interpretar
 * se deja tal cual — sigue siendo inválido y el usuario debe corregirlo.
 */
export function reformatEditedHours(
  editedHours: Map<string, string>,
  fromFormat: OvertimeHourFormat,
  toFormat: OvertimeHourFormat,
): Map<string, string> {
  const reformatted = new Map<string, string>();

  editedHours.forEach((text, rowId) => {
    const decimalValue = parseHoursInput(text, fromFormat);
    reformatted.set(
      rowId,
      decimalValue === null ? text : formatOvertimeDecimalHours(decimalValue, toFormat),
    );
  });

  return reformatted;
}
